/**
 * Reset a stale or expired cart session, WITHOUT creating a replacement order.
 *
 * The previous implementation called ensureOrderExists({ forceNew: true }) here,
 * minting a brand-new empty order before the customer had decided to re-add
 * anything. Most customers bail at the "please re-add your items" message, so
 * the row was simply abandoned: ~120 a week, 38% never touched again after
 * creation. It also inflated the funnel denominator by ~10 points, which is how
 * the regression was first noticed.
 *
 * An order is created by exactly one thing now: an explicit customer action.
 * The `if (!orderId)` branch in the upload handler already does this on the next
 * real upload, so nothing needs to be provisioned here.
 */
export interface StaleCartRecoveryDeps {
  /** Drops the localStorage backup of the order session. May throw on quota. */
  clearOrderData: () => void;
  /** Clears the shared React context + cookies pair. */
  setOrderInfo: (orderId: string | null, guestToken: string | null) => void;
  /** Clears the guest_token cookie. */
  setGuestTokenCookies: (token: string) => void;
  setSelectedOrderId: (orderId: string | null) => void;
  setSelectedGuestToken: (token: string | null) => void;
  /** Called once, after the reset, so the caller can inform the customer. */
  onRecovered: () => void;
}

export function recoverStaleCart(deps: StaleCartRecoveryDeps): void {
  // A failing localStorage clear (quota, private mode, evicted origin) must not
  // prevent the rest of the reset -- leaving the session half-cleared is how a
  // dead order id survives to be used again.
  try {
    deps.clearOrderData();
  } catch (error) {
    console.error("Error clearing stale order data from localStorage", error);
  }

  deps.setOrderInfo(null, null);
  deps.setGuestTokenCookies("");
  deps.setSelectedOrderId(null);
  deps.setSelectedGuestToken(null);

  deps.onRecovered();
}
